import experiment from 'experiment';
import React, { useState, useEffect, useContext } from 'react';
import type { Dispatch, MapDispatchToProps } from 'types/redux';
import * as util from '#packages/util';
import { fetchAppReadme } from '../../utils/fetchAppReadme';
import { getAppVersion } from '../../utils/utils';
import { usePrivateApps } from '../privateAppsProvider/privateAppsProvider';
import type { AppData } from '../privateAppsProvider/privateAppsProvider';
import { devCenterFacade } from '#packages/serverFacade';

type FetchReadmeParams = Parameters<typeof fetchAppReadme>;

interface OwnProps {
  initialAppDefId?: string;
  selectedVersion?: string;
  children: React.ReactNode;
}

interface DispatchProps {
  fetchReadme: (...params: FetchReadmeParams) => Promise<string>;
}

type Props = OwnProps & DispatchProps;

interface SelectedApp {
  app?: AppData;
  codePackage?: CodePackage;
  readme?: string;
  listNotes?: string[];
  description?: string;
  isLoading: boolean;
  selectedAppReady: boolean;
  selectedVersion?: string;
}

interface CodePackage {
  id: string;
  name: string;
}

const SelectedAppContext = React.createContext<{
  selectedApp?: SelectedApp;
  setSelectedApp?: (app: AppData, version?: string) => Promise<void>;
}>({});

const getAppCodePackage = ({
  components,
}: AppData): CodePackage | undefined => {
  const codePackageData = components?.find(
    ({ type }) => type === 'CODE_PACKAGE',
  )?.data as any;
  return (
    codePackageData && {
      id: codePackageData.id,
      name: codePackageData.importName,
    }
  );
};

function getInitialAppVersion(app: AppData, selectedVersion: string): string {
  if (experiment.isOpen('se_testAppWithDevVersion')) {
    if (util.appStudioUtils.isTestVersion(selectedVersion)) {
      return selectedVersion;
    }

    if (util.appStudioUtils.isTestVersion(app)) {
      return app.appFields.installedVersion;
    }

    if (!util.appStudioUtils.isAppMajorVersionBuilt(app.latestVersion)) {
      return util.appStudioUtils.TEST_VERSION.LATEST;
    }

    return getAppVersion(app) ?? app.latestVersion;
  }

  return app.appFields?.installedVersion ===
    util.appStudioUtils.TEST_VERSION.LATEST ||
    selectedVersion === util.appStudioUtils.TEST_VERSION.LATEST
    ? util.appStudioUtils.TEST_VERSION.LATEST
    : getAppVersion(app);
}

const SelectedAppProviderComponent: React.FC<Props> = ({
  children,
  fetchReadme,
  initialAppDefId,
  selectedVersion,
}) => {
  const cancelUpdate = React.useRef(false);
  const [selectedApp, _setSelectedApp] = useState<SelectedApp>({
    isLoading: false,
    selectedAppReady: false,
  });
  const { installedApps, availableApps } = usePrivateApps();

  const getAppData = (app: AppData, version?: string) =>
    devCenterFacade.getAppByVersion(
      app.appDefinitionId,
      version || app.latestVersion,
      app.latestVersion,
    );

  const getReadme = async (
    app: AppData,
    codePackage: CodePackage,
    version?: string,
  ) => {
    try {
      const readme = codePackage
        ? await fetchReadme({
            id: app.appDefinitionId,
            version: version || app.latestVersion,
          }).catch(() => '')
        : '';
      const readmeParsed = JSON.parse(readme);

      return readmeParsed.readmeFile.content;
    } catch (e) {
      return '';
    }
  };

  const setSelectedApp = async (app: AppData, version?: string) => {
    if (cancelUpdate.current) {
      return;
    }
    const codePackage = getAppCodePackage(app);
    _setSelectedApp({
      codePackage,
      isLoading: true,
      app,
      selectedAppReady: false,
      selectedVersion: version || getAppVersion(app),
    });

    try {
      const [{ listNotes, data }, readme] = await Promise.all([
        getAppData(app, version),
        getReadme(app, codePackage, version),
      ]);

      const { description } =
        data?.components.find(({ compType }) => compType === 'CODE_PACKAGE')
          ?.compData?.codePackageComponentData || {};

      _setSelectedApp((currentSelectedApp) => {
        if (currentSelectedApp.app !== app) {
          return currentSelectedApp;
        }
        return {
          app,
          listNotes,
          description,
          codePackage,
          readme,
          isLoading: false,
          selectedAppReady: !!app?.appDefinitionId,
          selectedVersion: version,
        };
      });
    } catch (e) {
      console.error(e);
      _setSelectedApp((currentSelectedApp) => {
        if (currentSelectedApp.app !== app) {
          return currentSelectedApp;
        }
        return {
          app,
          description: 'Error fetching data',
          isLoading: false,
          selectedAppReady: true,
        };
      });
    }
  };

  const setInitialApp = () => {
    const apps = [...installedApps, ...availableApps];

    const [app] = initialAppDefId
      ? apps.filter((app) => app.appDefinitionId === initialAppDefId)
      : apps;

    if (app) {
      const appVersion = getInitialAppVersion(app, selectedVersion);

      setSelectedApp(app, appVersion);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(setInitialApp, [availableApps.length, installedApps.length]);
  useEffect(
    () => () => {
      cancelUpdate.current = true;
    },
    [],
  );

  return (
    <SelectedAppContext.Provider value={{ selectedApp, setSelectedApp }}>
      {children}
    </SelectedAppContext.Provider>
  );
};

export const useSelectedApp = () => useContext(SelectedAppContext);

const mapDispatchToProps: MapDispatchToProps<DispatchProps, {}> = (
  dispatch: Dispatch,
): DispatchProps => ({
  fetchReadme: (...fetchReadmeArgs: FetchReadmeParams) =>
    dispatch(fetchAppReadme(...fetchReadmeArgs)),
});

export const SelectedAppProvider = util.hoc.connect(
  util.hoc.STORES.EDITOR_API,
  null,
  mapDispatchToProps,
)(SelectedAppProviderComponent);
